import omit from 'lodash/omit';

import { FILTER_STATUS_ID_MAP } from '@framework/ui/Organisms/Filters/constants';
import { toApiDate } from '@/creditflowresearch/application/helpers/zonedDayBounds';
import type {
  ICorporateBondScreenerGetTranchesRequest,
  TCorporateBondScreenerPerPage,
  TCorporateBondScreenerSortBy,
} from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerRequest';
import { EuropeanMarket, UsMarket } from '@/creditflowresearch/domain/Market';
import Region from '@/creditflowresearch/domain/Region';
import CorporateProduct from '@/creditflowresearch/domain/Product/entities/CorporateProduct';
import syncLinkedAgencies from '@/creditflowresearch/application/helpers/syncLinkedAgencies';
import { EXCLUDE_FLAG_KEYS } from './excludeFlagKeys';

type TFilters = ICorporateBondScreenerGetTranchesRequest['filters'];

export interface IBuildRequestOverrides {
  sortBy?: string,
  sortOrder?: string,
  page?: number,
  perPage?: number,
  search?: string | null,
  filters?: TFilters,
  columns?: string[],
}

const VALID_PER_PAGE: readonly TCorporateBondScreenerPerPage[] = [25, 50, 100, 200];

export const DEFAULT_FILTERS: TFilters = {
  dateFrom: null,
  dateTo: null,
  includedProductIds: [CorporateProduct.CORPORATE_PRODUCT_IDS[0]],
  includedMarketIds: [UsMarket.MARKET_ID],
  includedRegionIds: [],
  includedCountryIds: [],
  selectedCountryIds: [],
  includedCurrencyIds: [],
  excludedCurrencyIds: [],
  isCurrencyExcluded: false,
  includedIndustryIds: [],
  includedSubindustryIds: [],
  selectedSubindustryIds: [],
  includedStatusIds: [FILTER_STATUS_ID_MAP.priced],
  includedIssuerIds: [],
  excludedIssuerIds: [],
  includedParentIds: [],
  excludedParentIds: [],
  selectedParentIssuerOptions: [],
  isParentsIssuersExcluded: false,
  includedTickerCompanyIds: [],
  excludedTickerCompanyIds: [],
  selectedTickerOptions: [],
  isTickerExcluded: false,
  issuedByHoldingCompany: null,
  issuedByOperatingCompany: null,
  includedDealSizeFrom: null,
  includedDealSizeTo: null,
  includedTrancheSizeFrom: null,
  includedTrancheSizeTo: null,
  includedTenorFrom: null,
  includedTenorTo: null,
  includedMaturityDateFrom: null,
  includedMaturityDateTo: null,
  includedCallDateFrom: null,
  includedCallDateTo: null,
  includedRankingIds: [],
  includedRatingGroupIds: [],
  excludedRatingGroupIds: [],
  includedRatingAgencyIds: [],
  excludedRatingAgencyIds: [],
  includedPricingSpreadFrom: null,
  includedPricingSpreadTo: null,
  includedCouponFrom: null,
  includedCouponTo: null,
  includedCouponTypeIds: [],
  includedRegistrationGroupIds: [],
  includedBenchmarkGroupIds: [],
  includedLeadIds: [],
  excludedLeadIds: [],
  isLeadsExcluded: false,
  includedLeadRoleIds: [],
  dropped: false,
  includedEsgIds: [],
  includedUseOfProceedsIds: [],
  excludedUseOfProceedsIds: [],
  isUseOfProceedsExcluded: false,
  includedListingIds: [],
  includedIssuePriceFrom: null,
  includedIssuePriceTo: null,
  includedYieldFrom: null,
  includedYieldTo: null,
  includedXCoveredFrom: null,
  includedXCoveredTo: null,
  includedIptPxdFrom: null,
  includedIptPxdTo: null,
  includedAttritionFrom: null,
  includedAttritionTo: null,
  includedAdditionalFilters: [],
  excludedAdditionalFilters: [],
  isAdditionalFiltersExcluded: false,
  excludedTenorFrom: null,
  excludedTenorTo: null,
  isTenorExcluded: false,
  callable: null,
  perpetual: null,
  includeUsIgNonCountables: false,
};

const DEFAULT_REQUEST: ICorporateBondScreenerGetTranchesRequest = {
  filters: DEFAULT_FILTERS,
  search: null,
  sorting: { sortBy: 'date', sortOrder: 'desc' },
  paging: { page: 1, perPage: 25 },
};

export const UI_ONLY_FILTER_KEYS = ['selectedCountryIds', 'selectedSubindustryIds', 'selectedParentIssuerOptions', 'selectedTickerOptions', ...EXCLUDE_FLAG_KEYS] as const;

export const adaptFiltersToRequestFormat = (filters: TFilters) => {
  const apiFilters = omit(filters, UI_ONLY_FILTER_KEYS);

  return {
    ...apiFilters,
    dateFrom: toApiDate(apiFilters.dateFrom, 'start'),
    dateTo: toApiDate(apiFilters.dateTo, 'end'),
    includedMaturityDateFrom: toApiDate(apiFilters.includedMaturityDateFrom, 'start'),
    includedMaturityDateTo: toApiDate(apiFilters.includedMaturityDateTo, 'end'),
    includedCallDateFrom: toApiDate(filters.includedCallDateFrom, 'start'),
    includedCallDateTo: toApiDate(filters.includedCallDateTo, 'end'),
    includedRatingAgencyIds: syncLinkedAgencies(filters.includedRatingAgencyIds || []),
    excludedRatingAgencyIds: syncLinkedAgencies(filters.excludedRatingAgencyIds || []),
  };
};

// Single source of truth for cutting inaccessible markets from any corporate bond screener
// tranches request (SSR fetch, client fetch, export) - market access is region-derived.
// TODO: remove when the European market opening period ends
export const restrictToEuropeanMarket = <T extends { includedMarketIds: string[] }>(filters: T): T => ({
  ...filters,
  includedMarketIds: [EuropeanMarket.MARKET_ID],
});

export const restrictToAccessibleMarkets = <T extends { includedMarketIds: string[] }>(
  filters: T,
  accessibleRegionIds: string[],
  // TODO: remove when the European market opening period ends
  isRestrictedToEuropeanMarket: boolean = false,
): T => {
  // TODO: remove when the European market opening period ends
  if (isRestrictedToEuropeanMarket) {
    return restrictToEuropeanMarket(filters);
  }

  const accessibleMarketIds = Region.getMarketsByRegions(accessibleRegionIds).map((market) => market.getId());
  const allowedMarketIds = filters.includedMarketIds.filter((id) => accessibleMarketIds.includes(id));

  return {
    ...filters,
    includedMarketIds: allowedMarketIds.length > 0 ? allowedMarketIds : accessibleMarketIds,
  };
};

export const buildCorporateBondScreenerRequest = (
  overrides: IBuildRequestOverrides = {},
): ICorporateBondScreenerGetTranchesRequest => {
  const filters = overrides.filters ?? DEFAULT_REQUEST.filters;

  return {
    ...DEFAULT_REQUEST,
    filters: adaptFiltersToRequestFormat(filters),
    search: overrides.search ?? DEFAULT_REQUEST.search,
    sorting: {
      sortBy: (overrides.sortBy ?? DEFAULT_REQUEST.sorting.sortBy) as TCorporateBondScreenerSortBy,
      sortOrder: (overrides.sortOrder ?? DEFAULT_REQUEST.sorting.sortOrder) as 'asc' | 'desc',
    },
    paging: {
      page: overrides.page ?? DEFAULT_REQUEST.paging.page,
      perPage: VALID_PER_PAGE.includes(overrides.perPage as TCorporateBondScreenerPerPage)
        ? (overrides.perPage as TCorporateBondScreenerPerPage)
      : DEFAULT_REQUEST.paging.perPage,
    },
    columns: overrides.columns,
  };
};
